import type {
  ClassNamesGenerator,
  DatesSetArg,
  DayCellContentArg,
  EventClickArg,
  EventContentArg,
} from "@fullcalendar/core";
import type { EventSourceInput } from "@fullcalendar/core/index.js";
import jaLocale from "@fullcalendar/core/locales/ja";
import dayGridPlugin from "@fullcalendar/daygrid";
import _FullCalendar from "@fullcalendar/react";
import type { ReactNode, RefObject } from "react";

export type FullCalendarRef = _FullCalendar;

type Props = {
  /**
   * カレンダーに表示するイベントデータ
   */
  events: EventSourceInput;
  /**
   * イベントクリック時のハンドラー
   */
  onEventClick?: (arg: EventClickArg) => void;
  /**
   * イベントのコンテンツ
   */
  eventContent?: (arg: EventContentArg) => ReactNode;
  /**
   * 日付セルのクラス名（過去日など）
   */
  dayCellClassNames?: ClassNamesGenerator<DayCellContentArg>;
  /**
   * 日付セルのカスタムコンテンツ（クイック掲載の「＋」等）
   */
  dayCellContent?: (arg: DayCellContentArg) => ReactNode;
  /**
   * カレンダーの高さ
   * @default "auto"
   */
  height?: string | number;
  /**
   * FullCalendar インスタンスへの ref。外部からの prev/next/today API 呼び出しに使用
   */
  calendarRef?: RefObject<_FullCalendar | null>;
  /**
   * 表示月が変わったときのコールバック。カスタムヘッダーのタイトル同期に使用
   */
  onDatesSet?: (arg: DatesSetArg) => void;
  /**
   * ヘッダーツールバーの設定。false で非表示（カスタムヘッダーを使う場合）
   */
  headerToolbar?: false | { left?: string; center?: string; right?: string };
};

export function FullCalendar({
  events,
  onEventClick,
  eventContent,
  dayCellClassNames,
  dayCellContent,
  height = "auto",
  calendarRef,
  onDatesSet,
  headerToolbar,
}: Props) {
  const toolbar =
    headerToolbar !== undefined
      ? headerToolbar
      : { left: "prev today", center: "title", right: "next" };

  return (
    <_FullCalendar
      ref={calendarRef}
      plugins={[dayGridPlugin]}
      initialView="dayGridMonth"
      locale={jaLocale}
      events={events}
      eventClick={onEventClick}
      eventContent={eventContent}
      dayCellClassNames={dayCellClassNames}
      dayCellContent={dayCellContent}
      height={height}
      headerToolbar={toolbar}
      buttonText={{ today: "今日" }}
      datesSet={onDatesSet}
    />
  );
}
